import React, { ReactElement, Component, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { withRouter } from "react-router";
import { Col, Row, Select, Skeleton } from "antd";
import { SideFlyInContext } from "../../contexts/side-menu-fly-in";
import BreadCrumbs from "../../components/breadcrumb/breadcrumb";
import { LineChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Legend, Line, Tooltip } from "recharts";
import { item, ItemsContext } from "../../contexts/items";
import { ClusterSelectedContext } from "../../contexts/cluster-selected";
import { IsFreshContext } from "../../contexts/is_fresh";
import { http_get, http_request, destroy_worker } from "../../worker/http-func";
import { change_unit } from "../global/global";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "./cluster-overview.less";
import { cluster_info_type, ClusterInfoContext } from "../../contexts/cluster-info";
import { TypeNameContext } from "../../contexts/type_name";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { metric_values_new, metric_values } from "../../worker/function/interfaces";
import { UserInfoContext, IUserInfoType } from "../../contexts/user-info";
import { clusters_type_item, ClusterContext } from "../../contexts/clusters";
import { DatacenternameContext } from "../../contexts/datacenter-name";
import { UnitContext } from "../../contexts/unit";
import { formatDate } from "../../pages/user-management/user-util";
import { ThemeContext } from "../../contexts/theme";
import { TableListModalContext } from "../../components/table-list/table-list";
import TableListModal from "../../components/table-list-modal/table-list-modal";
import { table_list_modal_type, TableListModalTypeContext } from "../../contexts/table_list_modal";
import DropdownMenuSubscription from "../../components/dropdown-menu-subscription/dropdown-menu-subscription";
import { useDeepCompareMemo } from 'use-deep-compare';

const ClusterOverview: React.FC = (props: any): ReactElement<any, string | (new (props: any) => Component<any, any, any>)> | null => {
  const [clusters, set_clusters]: [clusters_type_item[], React.Dispatch<React.SetStateAction<clusters_type_item[]>>] = useContext(ClusterContext),
    user_info: IUserInfoType = useContext(UserInfoContext)[0],
    [cluster_info, set_cluster_info]: [cluster_info_type, React.Dispatch<React.SetStateAction<cluster_info_type>>] = useContext(ClusterInfoContext),
    [side_fly_in, set_side_fly_in]: [boolean, React.Dispatch<React.SetStateAction<boolean>>] = useContext(SideFlyInContext),
    [cluster_selected, set_cluster_selected]: [item | undefined, React.Dispatch<React.SetStateAction<item | undefined>>] = useContext(ClusterSelectedContext),
    [items, set_items]: [item[], React.Dispatch<React.SetStateAction<item[]>>] = useContext(ItemsContext),
    theme: string = useContext(ThemeContext)[0],
    [table_list_modal, set_table_list_modal]: [table_list_modal_type, React.Dispatch<React.SetStateAction<table_list_modal_type>>] = useContext(TableListModalTypeContext),
    [cluster_info_panel, set_cluster_info_panel]: [JSX.Element[], React.Dispatch<React.SetStateAction<JSX.Element[]>>] = useState([] as JSX.Element[]),
    [cluster_physical_resource_usage, set_cluster_physical_resource_usage]: [JSX.Element[], React.Dispatch<React.SetStateAction<JSX.Element[]>>] = useState(
      [] as JSX.Element[]
    ),
    [type_name, set_type_name]: [string | undefined, React.Dispatch<React.SetStateAction<string | undefined>>] = useContext(TypeNameContext),
    is_fresh: boolean = useContext(IsFreshContext)[0],
    unit_provide: string = useContext(UnitContext)[0],
    [metric_data, set_metric_data]: [metric_values_new, React.Dispatch<React.SetStateAction<metric_values_new>>] = useState({} as metric_values_new),
    datacenter_name: string | undefined = useContext(DatacenternameContext)[0],
    { formatMessage }: { formatMessage: any } = useDeepCompareMemo(() => props.intl, [props.intl]),
    { Option } = useMemo(() => Select, []);
  type metric_key_type = keyof metric_values;

  useEffect(
    () => () => {
      destroy_worker();
    },
    []
  );

  useEffect(() => {
    user_info.role !== "" &&
      datacenter_name &&
      http_get("cluster_overview_page", ["clusters", set_clusters], user_info.role, `${user_info.subscription_id}&type=${type_name}`, datacenter_name);
  }, [set_clusters, is_fresh, user_info.role, user_info.subscription_id, type_name, datacenter_name]);

  useEffect(() => {
    // 切换订阅号，当type_name为undefined
    !type_name &&
      (cluster_selected?.labels?.type === "dedicate" || cluster_selected?.labels?.type === "shared") &&
      set_type_name(cluster_selected?.labels?.type);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set_type_name, cluster_selected?.labels?.type]);

  useEffect(() => {
    if (user_info.role !== "" && cluster_selected?.name && datacenter_name) {
      const name: string = (cluster_selected || clusters[0]?.metadata)?.name || "";
      name &&
        http_request(
          "cluster_info",
          props,
          set_cluster_info,
          `/v1/datacenter/${datacenter_name}/cluster/${name}/info`,
          0,
          user_info.role,
          user_info.subscription_id,
          datacenter_name,
          name
        );
      http_request(
        "cluster_metric",
        props,
        set_metric_data,
        `/v1/datacenter/${datacenter_name}/cluster/${name}/metric`,
        0,
        user_info.role,
        user_info.subscription_id,
        datacenter_name,
        name
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_info.role, user_info.subscription_id, set_cluster_info, cluster_selected?.name, set_metric_data]);

  useEffect((): void => {
    if (Array.isArray(clusters)) {
      const clusters_new: item[] =
        clusters &&
        clusters.map((cluster: clusters_type_item) => {
          return (({ name, uid, key, selfLink, resourceVersion, creationTimestamp }) => ({
            name,
            uid,
            key,
            selfLink,
            resourceVersion,
            creationTimestamp,
          }))(cluster.metadata);
        });
      set_items(clusters_new);
    }
  }, [set_items, is_fresh, clusters]);

  const resource_on_click = useCallback((name: string | boolean) => {
    switch (name) {
      case "Nodes":
        props.history.push("/nodes");
        break;
      case "Workspaces":
        props.history.push("/cluster-info/workspaces");
        break;
      case "Namespaces":
        props.history.push("/workspace-info/namespaces");
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect((): void => {
    if (cluster_info) {
      const cluster_info_panel_new: JSX.Element[] = [],
        // cluster_info_new = cluster_selected as unknown as item | undefined,
        cluster_info_arr: string[] | undefined = [
          `${formatMessage({ id: "nodes" })}`,
          cluster_info.node ? cluster_info.node : "",
          `${formatMessage({ id: "workspaces" })}`,
          cluster_info.workspace ? cluster_info.workspace : "",
          `${formatMessage({ id: "namespaces" })}`,
          cluster_info.namespace ? cluster_info.namespace : "",
          `${formatMessage({ id: "user" })}`,
          cluster_info.user ? cluster_info.user : "",
          "Pod",
          cluster_info.usedPod ? Number(cluster_info.usedPod).toFixed(0) : "",
          "CPU",
          cluster_info.usedCPU ? `${Number(cluster_info.usedCPU).toFixed(2)} core` : "",
          `${formatMessage({ id: "memory" })}`,
          cluster_info.usedMemory ? `${change_unit(cluster_info.usedMemory, unit_provide, false).toFixed(2)} ${unit_provide}` : "",
          `${formatMessage({ id: "rootfs" })}`,
          cluster_info.usedRootfs ? `${change_unit(cluster_info.usedRootfs, unit_provide, false).toFixed(2)} ${unit_provide}` : "",
          `${formatMessage({ id: "pvc_disk" })}`,
          cluster_info.usedDisk ? `${change_unit(cluster_info.usedDisk, unit_provide, false).toFixed(2)} ${unit_provide}` : "",
          'GPU',
          cluster_info.usedGPU || "",
          `${formatMessage({ id: "create_time" })}`,
          cluster_info.updateTime && Number(cluster_info.updateTime) ? formatDate(Number(cluster_info.updateTime)) : "",
        ];

      for (let i = 0; i < cluster_info_arr.length; i += 2) {
        const is_nodes: boolean = cluster_info_arr[i] === `${formatMessage({ id: "nodes" })}`,
          is_workspaces: boolean = cluster_info_arr[i] === `${formatMessage({ id: "workspaces" })}`,
          is_namespaces: boolean = cluster_info_arr[i] === `${formatMessage({ id: "namespaces" })}`;
        if (!(cluster_info_arr[i] === "Nodes" && user_info.role === "workspaceOwner")) {
          cluster_info_panel_new[i] = (
            <Col
              className={`resource_card no-pad d-flex justify-center align-items-center ${cluster_info_arr[i] === "Created Time" ? "flex-2" : "flex-1"}`}
              key={cluster_info_arr[i]}
            >
              <Row
                className="resource_info d-flex border"
                // style={{ cursor: 'NodesWorkspacesNamespaces'.indexOf(cluster_info_arr[i]) >= 0 ? 'pointer' : 'default' }}
                // onClick={() => resource_on_click(cluster_info_arr[i])}>
                style={{ cursor: is_nodes || is_workspaces || is_namespaces ? "pointer" : "default" }}
                onClick={() => resource_on_click((is_nodes && "Nodes") || (is_workspaces && "Workspaces") || (is_namespaces && "Namespaces"))}
              >
                <Col className="strong text-ellipsis" title={cluster_info_arr[i + 1]}>
                  {cluster_info_arr[i + 1] || (
                    <Skeleton className="ant-col-sm-16 ant-col-sm-offset-4" title={{ width: "100%" }} paragraph={{ rows: 0 }} active />
                  )}
                </Col>
                <Col className="span info_title text-ellipsis">{cluster_info_arr[i]}</Col>
              </Row>
            </Col>
          );
        }
      }
      set_cluster_info_panel(cluster_info_panel_new);

      const cluster_physical_resource_usage_new_first: JSX.Element[] = [],
        cluster_physical_resource_usage_arr: string[] | undefined = [
          "CPU",
          cluster_info.usedCPU ? Number(cluster_info.usedCPU).toFixed(2) : "",
          cluster_info.totalCPU ? `${Number(cluster_info.totalCPU).toFixed(2)}` : "",
          "usedCPU",
          "Memory",
          cluster_info.usedMemory ? `${change_unit(cluster_info.usedMemory, unit_provide, false)}` : "",
          cluster_info.totalMemory ? `${change_unit(cluster_info.totalMemory, unit_provide, false)}` : "",
          "usedMemory",
          "Rootfs",
          cluster_info.usedRootfs ? `${change_unit(cluster_info.usedRootfs, unit_provide, false)}` : "",
          cluster_info.totalRootfs ? `${change_unit(cluster_info.totalRootfs, unit_provide, false)}` : "",
          "usedRootfs",
          "Pod",
          cluster_info.usedPod ? Number(cluster_info.usedPod).toFixed(0) : "",
          cluster_info.totalPod ? Number(cluster_info.totalPod).toFixed(0) : "",
          "usedPod",
          "PVC Disk",
          cluster_info.usedDisk ? `${change_unit(cluster_info.usedDisk, unit_provide, false)}` : "",
          cluster_info.totalDisk ? `${change_unit(cluster_info.totalDisk, unit_provide, false)}` : "",
          "usedDisk",
          // "GPU",
          // cluster_info.usedGPU ? `${change_unit(cluster_info.usedGPU, unit_provide, false)}` : "",
          // cluster_info.totalGPU ? `${change_unit(cluster_info.totalGPU, unit_provide, false)}` : "",
          // "usedGPU",
        ];

      let used: number, total: number, used_str: string, total_str: string, metric_key: metric_key_type, unit: string, metric_data_each: object[];

      for (let i = 0; i < cluster_physical_resource_usage_arr.length; i += 4) {
        const name = cluster_physical_resource_usage_arr[i];
        unit = name === "CPU" ? "core" : name === "Pod" ? "" : unit_provide;
        used_str = cluster_physical_resource_usage_arr[i + 1] === "N/A" ? "0" : cluster_physical_resource_usage_arr[i + 1];
        total_str =
          cluster_physical_resource_usage_arr[i + 2] === "N/A"
            ? "1"
            : Number(cluster_physical_resource_usage_arr[i + 2]) === -1
            ? "Unlimited"
            : cluster_physical_resource_usage_arr[i + 2];
        used = Number(used_str);
        total = Number(total_str);
        metric_key = cluster_physical_resource_usage_arr[i + 3] as metric_key_type;
        metric_data_each =
          metric_key === "usedCPU" || metric_key === "usedPod"
            ? metric_data[metric_key]?.data
            : metric_data[metric_key] && (metric_data[metric_key][unit_provide] as object[]);
        const interval: number =
          (metric_key === "usedCPU" || metric_key === "usedPod"
            ? metric_data[metric_key]?.interval
            : metric_data[metric_key] && (metric_data[metric_key].interval as number)) || 0;

        cluster_physical_resource_usage_new_first[i] = (
          <Col className="cluster_status_card" key={name}>
            <Row className="cluster_status_row">
              <Col sm={6}>
                <CircularProgressbar
                  value={total_str === "Unlimited" ? 0 : used}
                  maxValue={total === 0 || total_str === "Unlimited" ? Infinity : total}
                  strokeWidth={12}
                  text={`${total_str === "Unlimited" ? "Unlimited" : total === 0 ? "0.00%" : `${((used / total) * 100).toFixed(2)}%`}`}
                  className="progress_circle"
                  styles={buildStyles({ trailColor: `${theme === 'black_gray' ? 'dcdce0' : 'dcdce0'}` })}
                />
              </Col>
              <Col sm={18}>
                <Row className="status_detail">
                  <Col sm={24} className="font-w-6 text-ellipsis">
                    {formatMessage({ id: cluster_physical_resource_usage_arr[i].toLocaleLowerCase().replace(" ", "_") })}
                  </Col>
                  <Col sm={24} className="d-flex" title={`${used_str} / ${total ? total.toString() : total_str} ${unit}`}>
                    <span className="font-w-6 font-20 text-ellipsis">{used_str || <Skeleton title={{ width: "50px" }} paragraph={{ rows: 0 }} active />}</span>
                    <span className="px-6 vertical-middle">/</span>
                    <span className="text_total_span text-ellipsis">
                      {total ? total.toString() : total_str || <Skeleton className="ant-col pr-6" title={{ width: "50px" }} paragraph={{ rows: 0 }} active />}
                    </span>
                    <span className="pl-4 vertical-middle">{unit}</span>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="">
              <ResponsiveContainer width="90%" height={180}>
                <LineChart data={metric_data_each}>
                  <XAxis dataKey="time" />
                  <YAxis
                    label={{ value: `${unit}`, angle: 0, position: "insideTop" }}
                    domain={[(dataMin) => (dataMin - interval).toFixed(2), (dataMax) => (dataMax + interval / 2).toFixed(2)]}
                    allowDataOverflow={false}
                    interval="preserveStart"
                  />
                  <CartesianGrid strokeDasharray="3 3" />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dot={false}
                    dataKey={name}
                    stroke={theme === "dark_blue" ? "#2E7BBF" : theme === "black_gray" ? "#6c748b" : "#4fa1e8"}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Row>
          </Col>
        );
      }

      set_cluster_physical_resource_usage([
        <div key="phy_usage">
          <div className="grid grid_first">{cluster_physical_resource_usage_new_first}</div>
          <Row className="mt-20">{/* {physical_resource_quota_new} */}</Row>
        </div>,
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cluster_selected, clusters, is_fresh, items, unit_provide, metric_data, cluster_info, user_info.role, formatMessage, theme]);

  return (
    <TableListModalContext.Provider value={[table_list_modal, set_table_list_modal]}>
      <Col sm={24}>
        <TableListModal />
        <Row className="box_rounded card_title pointer breadcrumb_sticky">
          <Col sm={1}>
            {side_fly_in ? (
              <MenuUnfoldOutlined
                className="pl-10 mt-5"
                onClick={() => {
                  set_side_fly_in(!side_fly_in);
                }}
              />
            ) : (
              <MenuFoldOutlined
                className="pl-10 mt-5"
                onClick={() => {
                  set_side_fly_in(!side_fly_in);
                }}
              />
            )}
          </Col>
          <Col sm={22} pull={1} className="px-30">
            <BreadCrumbs />
          </Col>
        </Row>
        <Row className="px-6 mb-5">
          <Col xs={24} sm={12} lg={8} xl={9} xxl={8} className="">
            <DropdownMenuSubscription />
          </Col>
          <Col xs={24} sm={12} lg={8} xl={7} xxl={6} className="d-flex">
            <div style={{ lineHeight: 2 }} className="ml-20 font-14">
              <FormattedMessage id="cluster" />:
            </div>
            {clusters && clusters[0] && clusters[0].metadata && (
              <Select
                className="w-70 pl-20 mb-5"
                showSearch
                optionFilterProp="children"
                value={(cluster_selected || {}).name || sessionStorage.getItem("cluster") || clusters[0].metadata.name}
                onChange={(event: string) => {
                  const cluster_selected_new: item | undefined = (clusters.find((cluster: clusters_type_item) => cluster.metadata.name === event) || {}).metadata;
                  sessionStorage.setItem("cluster", (cluster_selected_new || {}).name || "");
                  set_cluster_selected(cluster_selected_new);
                }}
              >
                {Array.isArray(clusters) &&
                  clusters.map((cluster: clusters_type_item) => (
                    <Option key={cluster.metadata.name} value={cluster.metadata.name}>
                      {cluster.metadata.name}
                    </Option>
                  ))}
              </Select>
            )}
          </Col>
        </Row>
        <div className="px-6">
          <div className="card box_rounded border">
            <div className="card_title card_underline">
              <FormattedMessage id="resource_information" />
            </div>
            <div className="d-flex res_info">
              {cluster_info_panel}
              <LineChart />
            </div>
          </div>
        </div>
        <Row className="px-6">
          <Col lg={24} className="card box_rounded border">
            <div className="card_title card_underline">
              <FormattedMessage id="physical_resource_usage" />
            </div>
            {cluster_physical_resource_usage}
          </Col>
        </Row>
      </Col>
    </TableListModalContext.Provider>
  );
};

export default injectIntl(withRouter(ClusterOverview) as any);
